import React from "react";
import { ArrowUpRight } from "lucide-react";
import { BRAND_PHOTOS, FOUNDER_PHOTOS } from "@/config/site";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden"
    >
      {/* Soft gold spotlights */}
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[600px] rounded-full blur-3xl opacity-[0.10]"
        style={{ background: "radial-gradient(ellipse, #c6a263 0%, transparent 60%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full blur-3xl opacity-[0.06]"
        style={{ background: "radial-gradient(circle, #c6a263 0%, transparent 60%)" }}
      />

      {/* Decorative oversized UC mark, faint */}
      <div
        aria-hidden
        className="absolute -right-24 top-16 w-[640px] h-[640px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
        style={{ background: "radial-gradient(circle, #c6a263 0%, transparent 60%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <div className="fade-up delay-1 flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[rgb(var(--bone-dim))]">
            <span className="h-px w-10 bg-[rgb(var(--gold))]" />
            Uberti Creative — Vaughan / GTA
          </div>

          <h1
            data-testid="hero-headline"
            className="fade-up delay-2 mt-7 font-serif-display font-medium text-[38px] xs:text-[44px] sm:text-[60px] lg:text-[88px] leading-[1.02] text-[rgb(var(--bone))]"
          >
            Elevating brands
            <br />
            through content, strategy,
            <br />
            and{" "}
            <span className="italic text-gold-metal">design</span>.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="fade-up delay-3 mt-8 max-w-2xl text-base md:text-lg text-[rgb(var(--bone-dim))] leading-relaxed"
          >
            Uberti Creative helps businesses build a stronger digital presence
            through professional content creation, social media strategy,
            branding, and visual storytelling.
          </p>

          <div className="fade-up delay-4 mt-10 flex flex-wrap gap-3">
            <a
              href="#contact"
              data-testid="hero-start-project"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-medium btn-gold"
            >
              Start a Project
              <ArrowUpRight size={16} />
            </a>
            <a
              href="#services"
              data-testid="hero-view-services"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm font-medium btn-ghost-gold"
            >
              View Services
            </a>
          </div>

          <div className="fade-up delay-5 mt-14 grid grid-cols-3 max-w-xl gap-6 border-t border-[rgba(198,162,99,0.18)] pt-8">
            {[
              { k: "Content", v: "Built to perform on social" },
              { k: "Strategy", v: "Plans tailored to the brand" },
              { k: "Design", v: "Premium, consistent visuals" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-cinzel text-[11px] tracking-[0.3em] uppercase text-[rgb(var(--gold-soft))]">
                  {s.k}
                </div>
                <div className="mt-2 text-[13px] leading-snug text-[rgb(var(--bone-dim))]">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right column — engraved brand sign photograph */}
        <div className="hidden lg:flex lg:col-span-4 justify-center">
          <div className="relative w-[380px] h-[460px] rounded-2xl border border-[rgba(198,162,99,0.22)] bg-[#0b0a08] overflow-hidden logo-glow">
            <img
              src={FOUNDER_PHOTOS.seatedDesk}
              alt="Mason Uberti at the Uberti Creative studio desk"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "50% 30%" }}
            />
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.85) 100%)",
              }}
            />
            <div className="absolute bottom-6 inset-x-6">
              <div className="text-[11px] tracking-[0.32em] uppercase text-[rgb(var(--gold-soft))]">
                The Studio
              </div>
              <div className="mt-2 font-serif-display italic text-lg text-[rgb(var(--bone))]">
                Uberti Creative · est. 2025
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
